import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { heroShowcaseMovies } from '@/data/dashboardShowcaseMovies';

function MosaicVideoCell({ movie, index, showTitles }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = true;
    const tryPlay = () => {
      el.play().catch(() => {});
    };
    tryPlay();
    el.addEventListener('loadeddata', tryPlay);
    return () => el.removeEventListener('loadeddata', tryPlay);
  }, [movie.videoUrl]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
      className="relative h-full w-full overflow-hidden bg-black"
      data-testid={`video-mosaic-${movie.id}`}
    >
      <video
        ref={videoRef}
        src={movie.videoUrl}
        muted
        loop
        playsInline
        autoPlay
        preload="auto"
        className="absolute inset-0 h-full w-full object-cover"
      />
      {showTitles ? (
        <>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
          <div className="pointer-events-none absolute bottom-3 left-3 right-3">
            <p className="text-xs sm:text-sm font-heading font-bold text-white drop-shadow-lg line-clamp-1">
              {movie.title}
            </p>
            <p className="text-[10px] sm:text-xs text-white/75 line-clamp-1">{movie.style}</p>
          </div>
        </>
      ) : null}
    </motion.div>
  );
}

const OVERLAY = {
  landing: (
    <>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0a0a0f] via-[#0a0a0f]/75 to-[#0a0a0f]/25 sm:via-[#0a0a0f]/55 sm:to-transparent" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0a0a0f]/80 via-transparent to-[#0a0a0f]/40" />
    </>
  ),
  auth: (
    <>
      <div className="pointer-events-none absolute inset-0 bg-[#0a0a0f]/72" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0a0a0f]/90 via-[#0a0a0f]/55 to-[#0a0a0f]/90" />
    </>
  ),
};

/**
 * Full-bleed 2×2 autoplay video mosaic for marketing / auth shells.
 * @param {{ variant?: 'landing' | 'auth', showTitles?: boolean, className?: string }} props
 */
export function VideoMosaicBackground({ variant = 'landing', showTitles, className = '' }) {
  const titles = showTitles ?? variant === 'landing';

  return (
    <div
      className={`absolute inset-0 grid grid-cols-2 grid-rows-2 ${className}`.trim()}
      data-testid={`video-mosaic-background-${variant}`}
      aria-hidden
    >
      {heroShowcaseMovies.map((movie, i) => (
        <MosaicVideoCell key={movie.id} movie={movie} index={i} showTitles={titles} />
      ))}
      {OVERLAY[variant] || OVERLAY.landing}
    </div>
  );
}
